import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CalendarDays, CalendarOff, ChevronLeft, ChevronRight, Coffee, Clock,
  Footprints, List, Printer, RefreshCw, Sparkles, Users, X,
} from 'lucide-react';
import WalkInDialog from './WalkInDialog';
import StaffTimeOffDialog from './StaffTimeOffDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 20000;
const PX_PER_MIN = 1.6;          // 48px per 30-minute slot
const LANE_WIDTH = 190;

const STATUS_BLOCK = {
  confirmed: 'bg-blue-500/90 border-blue-600',
  scheduled: 'bg-blue-500/90 border-blue-600',
  in_progress: 'bg-indigo-500/90 border-indigo-600',
  completed: 'bg-slate-400/90 border-slate-500',
  pending_payment: 'bg-amber-500/90 border-amber-600',
};

const todayISO = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kuwait', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || '0:0').split(':').map(Number);
  return h * 60 + (m || 0);
};

const hoursLabel = (minutes, t) => {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  if (!h && !m) return `0${t('schedule.hourShort', 'h')}`;
  return `${h ? `${h}${t('schedule.hourShort', 'h')} ` : ''}${m ? `${m}${t('schedule.minuteShort', 'm')}` : ''}`.trim();
};

const StaffTimelineBoard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const timeFormatter = new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-KW' : 'en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || todayISO();
  const token = localStorage.getItem('sessionToken');

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newIds, setNewIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stale, setStale] = useState(false);
  const [walkInSlot, setWalkInSlot] = useState(null);
  const [walkInPrefill, setWalkInPrefill] = useState(null);
  const [timeOffStaff, setTimeOffStaff] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [moveRequest, setMoveRequest] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [toast, setToast] = useState('');
  const knownIds = useRef(null);
  const boardRef = useRef(null);
  const scrollRef = useRef(null);

  const setDate = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', next);
    setSearchParams(params, { replace: true });
  };

  const shiftDate = (days) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + days);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  // Tap an empty part of a lane to log a walk-in at that minute
  const snapTo15 = (minutes) => Math.max(0, Math.round(minutes / 15) * 15);

  const minutesFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - rect.top;
    return snapTo15((boardRef.current?.axis?.start_minutes || 0) + offsetY / PX_PER_MIN);
  };

  const openWalkIn = (lane, event) => {
    if (lane.is_unassigned) return;
    const minutes = minutesFromEvent(event);
    setWalkInSlot({
      staffId: lane.staff.id,
      staffName: lane.staff.name,
      time: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
    });
  };

  const handleDrop = (lane, event) => {
    event.preventDefault();
    if (!dragging || lane.is_unassigned) return;
    const minutes = minutesFromEvent(event);
    const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    setDragging(null);
    if (lane.staff.id === dragging.staffId && time === dragging.apt.start_time) return;
    setMoveError('');
    setNotifyCustomer(true);
    setMoveRequest({ apt: dragging.apt, fromStaff: dragging.staffName, toStaff: lane.staff, time });
  };

  const confirmMove = async () => {
    setMoveBusy(true);
    setMoveError('');
    try {
      await axios.put(`${API}/provider/appointments/${moveRequest.apt.id}/move`, {
        appointment_time: moveRequest.time,
        appointment_date: date,
        staff_id: moveRequest.toStaff.id,
        notify_customer: notifyCustomer,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMoveRequest(null);
      setSelected(null);
      setToast(notifyCustomer
        ? t('schedule.movedAndNotified', 'Booking moved — the customer has been told')
        : t('schedule.moved', 'Booking moved'));
      setTimeout(() => setToast(''), 5000);
      fetchBoard(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === 'slot_taken') setMoveError(t('schedule.slotTaken', 'That slot is already booked'));
      else if (detail?.error === 'staff_unavailable') {
        setMoveError(detail.reason === 'outside_hours'
          ? t('schedule.outsideHours', 'That is outside the opening hours')
          : t('schedule.staffUnavailable', 'That staff member is not available then'));
      } else setMoveError(typeof detail === 'string' ? detail : t('schedule.moveFailed', 'Could not move the booking'));
    } finally {
      setMoveBusy(false);
    }
  };

  const fetchBoard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/daily-schedule/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date },
      });
      const ids = (res.data.lanes || []).flatMap((l) => l.appointments.map((a) => a.id));
      if (knownIds.current) {
        const fresh = ids.filter((id) => !knownIds.current.includes(id));
        if (fresh.length) {
          setNewIds((prev) => [...new Set([...prev, ...fresh])]);
          setTimeout(() => setNewIds((prev) => prev.filter((id) => !fresh.includes(id))), 60000);
        }
      }
      knownIds.current = ids;
      boardRef.current = res.data;
      setBoard(res.data);
      setLastUpdated(new Date());
      setError('');
      setStale(false);
    } catch (e) {
      if (silent && boardRef.current) setStale(true);
      else setError(e.response?.data?.detail || t('schedule.loadFailed', 'Could not load the schedule'));
    } finally {
      setLoading(false);
    }
  }, [token, date, t]);

  useEffect(() => {
    knownIds.current = null;
    boardRef.current = null;
    setNewIds([]);
    setSelected(null);
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') fetchBoard(true); };
    const id = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [fetchBoard]);

  // Bring "now" into view on today's board
  useEffect(() => {
    if (board?.now_minutes && scrollRef.current) {
      const offset = (board.now_minutes - board.axis.start_minutes) * PX_PER_MIN - 120;
      scrollRef.current.scrollTop = Math.max(0, offset);
    }
  }, [board?.date, board?.now_minutes, board?.axis?.start_minutes]);

  const axisStart = board?.axis?.start_minutes ?? 0;
  const axisEnd = board?.axis?.end_minutes ?? 0;
  const gridHeight = Math.max(0, (axisEnd - axisStart) * PX_PER_MIN);
  const slots = [];
  for (let m = axisStart; m <= axisEnd; m += board?.axis?.slot_minutes || 30) slots.push(m);
  const top = (minutes) => (minutes - axisStart) * PX_PER_MIN;

  return (
    <div className={embedded ? 'print:bg-white' : 'min-h-screen bg-slate-50 pb-10 print:bg-white'} data-testid="staff-timeline-page">
      <div className={embedded ? '' : 'mx-auto max-w-7xl px-4 pt-6'}>
        {!embedded && (
          <button
            onClick={() => navigate('/provider-dashboard?tab=staff')}
            data-testid="timeline-back-btn"
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 print:hidden"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('schedule.backToDashboard', 'Back to dashboard')}
          </button>
        )}

        <header className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">{t('schedule.dayBoard', 'Day board')}</p>
              <h1 className="mt-1 truncate text-2xl font-bold text-slate-900" data-testid="timeline-title">
                {board?.provider?.name || t('schedule.dailySchedule', 'Daily schedule')}
              </h1>
              {board && (
                <>
                <p className="text-sm text-slate-500" data-testid="timeline-totals">
                  <span className="numeric-ltr">{board.totals.total_bookings}</span> {t('schedule.bookings', 'bookings')}
                  {' · '}<span className="numeric-ltr">{board.totals.staff_working}</span> {t('schedule.working', 'working')}
                  {board.totals.staff_off > 0 && (
                    <>{' · '}<span className="numeric-ltr">{board.totals.staff_off}</span> {t('schedule.staffOff', 'off')}</>
                  )}
                  {' · '}
                  <span className="numeric-ltr">{Math.floor((board.totals.booked_minutes || 0) / 60)}</span>{t('schedule.hourShort', 'h')}
                  {board.totals.booked_minutes % 60 > 0 && (
                    <> <span className="numeric-ltr">{board.totals.booked_minutes % 60}</span>{t('schedule.minuteShort', 'm')}</>
                  )}
                  {' '}{t('schedule.bookedHours', 'booked')}
                </p>
                {board.totals.walk_ins > 0 && (
                  <button
                    onClick={() => navigate('/provider/walk-ins')}
                    data-testid="timeline-walk-in-takings"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100 print:hidden"
                  >
                    <Footprints className="h-3.5 w-3.5" />
                    {t('walkIn.countLabel', { count: board.totals.walk_ins })}
                    {' · '}
                    <span className="numeric-ltr">{board.totals.walk_in_takings} {t('common.currency', 'KWD')}</span>
                    <span className="font-semibold text-amber-600">{t('walkIn.noCommissionShort', 'no commission')}</span>
                  </button>
                )}
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 print:hidden">
              <button
                onClick={() => navigate(`/provider/schedule/list?date=${date}`)}
                data-testid="timeline-list-view-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <List className="h-3.5 w-3.5" /> {t('schedule.listView', 'List view')}
              </button>
              <button
                onClick={() => fetchBoard(true)}
                data-testid="timeline-refresh-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {t('schedule.refresh', 'Refresh')}
              </button>
              <button
                onClick={() => window.print()}
                data-testid="timeline-print-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <Printer className="h-3.5 w-3.5" /> {t('schedule.print', 'Print')}
              </button>
            </div>
          </div>
          <p className="mt-3 text-[11px] font-semibold text-slate-400 print:hidden" data-testid="timeline-interaction-hint">
            {t('schedule.tapEmptyHint', 'Tap an empty slot to add a walk-in · drag a booking to move it')}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={() => shiftDate(-1)}
              data-testid="timeline-prev-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </button>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayISO())}
              data-testid="timeline-date-picker"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800"
            />
            <button
              onClick={() => shiftDate(1)}
              data-testid="timeline-next-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
            {date !== todayISO() && (
              <button
                onClick={() => setDate(todayISO())}
                data-testid="timeline-today-btn"
                className="rounded-full bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
              >
                {t('schedule.today', 'Today')}
              </button>
            )}
            {board?.is_today && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700" data-testid="timeline-today-badge">
                <Sparkles className="h-3.5 w-3.5" /> {t('schedule.today', 'Today')}
              </span>
            )}
          </div>
        </header>

        {loading && (
          <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="timeline-loading">
            {t('schedule.loadingBoard', 'Loading the board…')}
          </div>
        )}

        {!loading && error && (
          <div className="mt-4 rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm font-semibold text-rose-700" data-testid="timeline-error">
            {error}
          </div>
        )}

        {!loading && !error && board && board.lanes.length === 0 && (
          <div className="mt-4 rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="timeline-no-staff">
            <Users className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="font-bold text-slate-900">{t('schedule.noStaff', 'No staff members yet')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('schedule.noStaffHint', 'Add your team in the dashboard and their day will show up here.')}</p>
          </div>
        )}

        {!loading && !error && board && board.lanes.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            <div ref={scrollRef} className="max-h-[70vh] overflow-auto print:max-h-none">
              <div style={{ minWidth: 56 + board.lanes.length * LANE_WIDTH }}>
                {/* Staff header row — sticky, inside the same scroller so it tracks sideways */}
                <div className="sticky top-0 z-30 flex border-b border-slate-200 bg-slate-50">
                  <div className="sticky start-0 z-40 w-14 shrink-0 border-e border-slate-200 bg-slate-50 sm:w-16" />
                  {board.lanes.map((lane) => (
                    <div
                      key={lane.staff.id}
                      className="flex-1 border-e border-slate-200 px-3 py-3 text-center last:border-e-0"
                      style={{ minWidth: LANE_WIDTH }}
                      data-testid={`timeline-lane-header-${lane.staff.id}`}
                    >
                      <button
                        onClick={() => navigate(`/provider/schedule/list/${lane.staff.id}?date=${date}`)}
                        className="w-full truncate text-sm font-bold text-slate-900 hover:text-blue-700 hover:underline"
                      >
                        {lane.staff.name}
                      </button>
                      <p className="mt-0.5 text-[11px] font-semibold text-slate-400">
                        {lane.is_day_off
                          ? t('schedule.dayOff', 'Day off')
                          : <><span className="numeric-ltr">{lane.summary.total_bookings}</span> {t('schedule.booked', 'booked')} · <span className="numeric-ltr">{lane.summary.utilisation_pct}%</span></>}
                      </p>
                      {!lane.is_unassigned && (
                        <button
                          onClick={() => setTimeOffStaff(lane.staff)}
                          data-testid={`timeline-time-off-btn-${lane.staff.id}`}
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 hover:bg-slate-200 hover:text-slate-700 print:hidden"
                        >
                          <CalendarOff className="h-3 w-3" /> {t('schedule.timeOff', 'Time off')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex">
                  {/* Time gutter */}
                  <div className="sticky start-0 z-20 w-14 shrink-0 border-e border-slate-200 bg-white sm:w-16">
                    <div className="relative" style={{ height: gridHeight }}>
                      {slots.map((m) => (
                        <div
                          key={m}
                          className="numeric-ltr absolute -translate-y-1/2 ps-1 text-[11px] font-semibold text-slate-400"
                          style={{ top: top(m) }}
                        >
                          {m % 60 === 0 ? `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:00` : ''}
                        </div>
                      ))}
                    </div>
                  </div>

              {/* Lanes */}
              <div className="relative flex-1" style={{ minWidth: board.lanes.length * LANE_WIDTH }}>
                <div className="pointer-events-none absolute inset-0" style={{ height: gridHeight }}>
                  {slots.map((m) => (
                    <div
                      key={m}
                      className={`absolute w-full border-t ${m % 60 === 0 ? 'border-slate-200' : 'border-slate-100'}`}
                      style={{ top: top(m) }}
                    />
                  ))}
                </div>

                <div className="flex" style={{ height: gridHeight }}>
                  {board.lanes.map((lane) => {
                    const workStart = lane.working_hours.start ? toMinutes(lane.working_hours.start) : null;
                    const workEnd = lane.working_hours.end ? toMinutes(lane.working_hours.end) : null;
                    return (
                      <div
                        key={lane.staff.id}
                        className="relative flex-1 border-e border-slate-200 last:border-e-0"
                        style={{ minWidth: LANE_WIDTH }}
                        data-testid={`timeline-lane-${lane.staff.id}`}
                        onClick={(e) => { if (e.target === e.currentTarget && !lane.is_day_off) openWalkIn(lane, e); }}
                        onDragOver={(e) => { if (dragging && !lane.is_unassigned) e.preventDefault(); }}
                        onDrop={(e) => handleDrop(lane, e)}
                      >
                        {/* Off-hours shading */}
                        {lane.is_day_off ? (
                          <div className="absolute inset-0 bg-slate-100/70" data-testid={`timeline-day-off-${lane.staff.id}`}>
                            <p className="mt-6 text-center text-xs font-bold text-slate-400">
                              {lane.day_off_reason || t('schedule.dayOff', 'Day off')}
                            </p>
                          </div>
                        ) : (
                          <>
                            {workStart !== null && workStart > axisStart && (
                              <div className="absolute inset-x-0 bg-slate-100/70" style={{ top: 0, height: top(workStart) }} />
                            )}
                            {workEnd !== null && workEnd < axisEnd && (
                              <div className="absolute inset-x-0 bg-slate-100/70" style={{ top: top(workEnd), height: gridHeight - top(workEnd) }} />
                            )}
                            {lane.working_hours.break_start && lane.working_hours.break_end && (
                              <div
                                className="absolute inset-x-1 flex items-center justify-center rounded-lg bg-amber-50 text-[10px] font-bold uppercase tracking-wide text-amber-600"
                                style={{
                                  top: top(toMinutes(lane.working_hours.break_start)),
                                  height: (toMinutes(lane.working_hours.break_end) - toMinutes(lane.working_hours.break_start)) * PX_PER_MIN,
                                }}
                                data-testid={`timeline-break-${lane.staff.id}`}
                              >
                                <Coffee className="me-1 h-3 w-3" /> {t('schedule.break', 'Break')}
                              </div>
                            )}
                            {lane.partial_days_off.map((off) => off.start_time && off.end_time && (
                              <div
                                key={`${off.start_time}-${off.end_time}`}
                                data-testid={`timeline-partial-off-${lane.staff.id}`}
                                className="absolute inset-x-1 rounded-lg bg-slate-200/80 text-center text-[10px] font-bold uppercase text-slate-500"
                                style={{
                                  top: top(toMinutes(off.start_time)),
                                  height: (toMinutes(off.end_time) - toMinutes(off.start_time)) * PX_PER_MIN,
                                }}
                              >
                                {t('schedule.off', 'Off')}
                              </div>
                            ))}
                          </>
                        )}

                        {/* Bookings */}
                        {lane.appointments.map((apt) => {
                          const start = toMinutes(apt.start_time);
                          const height = Math.max(22, apt.duration * PX_PER_MIN - 2);
                          const isNew = newIds.includes(apt.id);
                          return (
                            <button
                              key={apt.id}
                              onClick={() => setSelected({ ...apt, staff: lane.staff })}
                              draggable={!lane.is_unassigned}
                              onDragStart={() => setDragging({ apt, staffId: lane.staff.id, staffName: lane.staff.name })}
                              onDragEnd={() => setDragging(null)}
                              data-testid={`timeline-booking-${apt.id}`}
                              className={`absolute inset-x-1 cursor-grab overflow-hidden rounded-lg border-s-4 p-1.5 text-start text-white shadow-sm transition-transform hover:z-10 hover:scale-[1.02] active:cursor-grabbing ${
                                apt.is_walk_in ? 'border-amber-600 bg-amber-500/95' : (STATUS_BLOCK[apt.status] || 'bg-slate-500/90 border-slate-600')
                              } ${isNew ? 'ring-2 ring-emerald-400' : ''} ${dragging?.apt?.id === apt.id ? 'opacity-50' : ''}`}
                              style={{ top: top(start), height }}
                            >
                              <p className="numeric-ltr truncate text-[11px] font-bold leading-tight">
                                {apt.start_time} {apt.customer_first_name}
                              </p>
                              {height > 34 && (
                                <p className="truncate text-[10px] leading-tight opacity-90">
                                  {apt.is_walk_in ? `${t('walkIn.badge', 'Walk-in')} · ` : ''}{apt.service_name}
                                </p>
                              )}
                              {isNew && (
                                <span className="absolute end-1 top-1 rounded-full bg-emerald-500 px-1 text-[9px] font-bold" data-testid={`timeline-new-badge-${apt.id}`}>
                                  {t('schedule.newBadge', 'New')}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Now line */}
                {board.now_minutes !== null && board.now_minutes >= axisStart && board.now_minutes <= axisEnd && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-rose-500"
                    style={{ top: top(board.now_minutes) }}
                    data-testid="timeline-now-line"
                  >
                    <span className="absolute -top-2 start-0 h-3 w-3 rounded-full bg-rose-500" />
                  </div>
                )}
                </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {lastUpdated && (
          <p className="mt-5 text-center text-xs text-slate-400 print:hidden" data-testid="timeline-last-updated">
            {stale ? (
              <span className="font-semibold text-amber-600">
                {t('schedule.offlineHint', 'Can’t reach the server — showing the {{time}} version', { time: timeFormatter.format(lastUpdated) })}
              </span>
            ) : (
              <>{t('schedule.updated', 'Updated {{time}} · refreshes automatically', { time: timeFormatter.format(lastUpdated) })}</>
            )}
          </p>
        )}
      </div>

      {/* Booking details */}
      {selected && (
        <div
          className="fixed inset-0 z-50 grid place-items-end bg-slate-900/50 p-0 backdrop-blur-sm sm:place-items-center sm:p-4"
          onClick={() => setSelected(null)}
          data-testid="timeline-details-overlay"
        >
          <div
            className="w-full rounded-t-3xl bg-white p-5 sm:max-w-md sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="timeline-details-panel"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-blue-600">{t('schedule.booking', 'Booking')}</p>
                <h3 className="mt-1 text-xl font-bold text-slate-900" data-testid="timeline-details-service">
                  {selected.service_name}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="timeline-details-close-btn"
                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">{t('schedule.customer', 'Customer')}</dt>
                <dd className="font-semibold text-slate-900" data-testid="timeline-details-customer">{selected.customer_first_name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">{t('schedule.time', 'Time')}</dt>
                <dd className="numeric-ltr font-semibold text-slate-900" data-testid="timeline-details-time">
                  {selected.start_time}–{selected.end_time} · {selected.duration} {t('schedule.minutes', 'min')}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">{t('schedule.staff', 'Staff')}</dt>
                <dd className="font-semibold text-slate-900">{selected.staff?.name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">{t('schedule.status', 'Status')}</dt>
                <dd className="font-semibold text-slate-900" data-testid="timeline-details-status">
                  {t(`schedule.statuses.${selected.status}`, (selected.status || '').replace(/_/g, ' '))}
                </dd>
              </div>
              {selected.reference_number && (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">{t('schedule.reference', 'Reference')}</dt>
                  <dd className="numeric-ltr font-mono text-xs text-slate-500">{selected.reference_number}</dd>
                </div>
              )}
              {(selected.addon_names || []).length > 0 && (
                <div className="flex justify-between gap-4" data-testid="timeline-details-addons">
                  <dt className="text-slate-500">{t('addOns.summaryTitle', 'Extras')}</dt>
                  <dd className="text-end font-semibold text-amber-700">{selected.addon_names.join(' · ')}</dd>
                </div>
              )}
            </dl>

            <div className="mt-5 flex flex-col gap-2">
              <button
                onClick={() => navigate(`/provider/schedule/list/${selected.staff?.id}?date=${date}`)}
                data-testid="timeline-details-open-staff-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
              >
                <CalendarDays className="h-4 w-4" /> {t('schedule.openDay', 'Open {{name}}’s day', { name: selected.staff?.name?.split(' ')[0] })}
              </button>
              {selected.is_walk_in && (
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/provider/walk-in/${selected.id}/repeat`, {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { date },
                      });
                      setSelected(null);
                      setWalkInPrefill(res.data);
                      setWalkInSlot({
                        staffId: res.data.staff_id || selected.staff?.id,
                        staffName: selected.staff?.name,
                        time: res.data.suggested_time || selected.start_time,
                      });
                    } catch {
                      setToast(t('walkIn.repeatFailed', 'Could not load that customer'));
                      setTimeout(() => setToast(''), 5000);
                    }
                  }}
                  data-testid="timeline-details-repeat-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
                >
                  <Footprints className="h-4 w-4" /> {t('walkIn.bookAgain', 'Book again')}
                </button>
              )}
              {!selected.staff?.id?.startsWith('unassigned') && (
                <button
                  onClick={() => {
                    setMoveError('');
                    setNotifyCustomer(true);
                    setMoveRequest({
                      apt: selected,
                      fromStaff: selected.staff?.name,
                      toStaff: selected.staff,
                      time: selected.start_time,
                      pickTime: true,
                    });
                  }}
                  data-testid="timeline-details-move-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  <Clock className="h-4 w-4" /> {t('schedule.moveBooking', 'Move this booking')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm a move (drag-and-drop or the Move button) */}
      {moveRequest && (
        <div className="fixed inset-0 z-[60] grid place-items-end bg-slate-900/50 sm:place-items-center sm:p-4" data-testid="move-confirm-dialog">
          <div className="w-full rounded-t-3xl bg-white p-5 sm:max-w-md sm:rounded-2xl">
            <h3 className="text-lg font-bold text-slate-900">{t('schedule.moveTitle', 'Move this booking?')}</h3>
            <p className="numeric-ltr mt-1 text-sm text-slate-600" data-testid="move-confirm-summary">
              {moveRequest.apt.customer_first_name} · {moveRequest.apt.service_name}
              <br />
              {moveRequest.apt.start_time} → <strong>{moveRequest.time}</strong>
              {moveRequest.toStaff?.name ? ` · ${moveRequest.toStaff.name}` : ''}
            </p>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600">{t('schedule.newTime', 'New time')}</label>
                <input
                  type="time"
                  className="numeric-ltr w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={moveRequest.time}
                  onChange={(e) => setMoveRequest({ ...moveRequest, time: e.target.value })}
                  data-testid="move-time-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600">{t('schedule.staff', 'Staff')}</label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={moveRequest.toStaff?.id || ''}
                  onChange={(e) => {
                    const lane = board.lanes.find((l) => l.staff.id === e.target.value);
                    setMoveRequest({ ...moveRequest, toStaff: lane ? lane.staff : moveRequest.toStaff });
                  }}
                  data-testid="move-staff-select"
                >
                  {board.lanes.filter((l) => !l.is_unassigned).map((l) => (
                    <option key={l.staff.id} value={l.staff.id}>{l.staff.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                data-testid="move-notify-checkbox"
                className="h-4 w-4 rounded border-slate-300"
              />
              {t('schedule.notifyCustomer', 'Tell the customer about the new time')}
            </label>

            {moveError && (
              <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="move-error">{moveError}</p>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => { setMoveRequest(null); setMoveError(''); }}
                data-testid="move-cancel-btn"
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                {t('schedule.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmMove}
                disabled={moveBusy}
                data-testid="move-confirm-btn"
                className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {moveBusy ? t('schedule.moving', 'Moving…') : t('schedule.confirmMove', 'Move it')}
              </button>
            </div>
          </div>
        </div>
      )}

      {walkInSlot && (
        <WalkInDialog
          slot={walkInSlot}
          date={date}
          prefill={walkInPrefill}
          onClose={() => { setWalkInSlot(null); setWalkInPrefill(null); }}
          onCreated={() => {
            setWalkInSlot(null);
            setWalkInPrefill(null);
            setToast(t('walkIn.added', 'Walk-in added — no commission on this one'));
            setTimeout(() => setToast(''), 5000);
            fetchBoard(true);
          }}
        />
      )}

      {timeOffStaff && (
        <StaffTimeOffDialog
          staff={timeOffStaff}
          date={date}
          onClose={() => setTimeOffStaff(null)}
          onSaved={() => {
            setTimeOffStaff(null);
            setToast(t('timeOff.saved', 'Time off saved'));
            setTimeout(() => setToast(''), 5000);
            fetchBoard(true);
          }}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 start-1/2 z-[70] -translate-x-1/2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-xl print:hidden"
          data-testid="board-toast"
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default StaffTimelineBoard;
